import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { intakeAPI, aiAPI, adminAPI } from '../../lib/api';
import {
  ArrowLeft,
  Heart,
  Pill,
  FirstAid,
  Robot,
  CheckCircle,
  Warning,
  Storefront,
  MagnifyingGlass,
  Plus,
  Trash,
  SpinnerGap,
  FloppyDisk,
  Stethoscope,
  ClipboardText
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function CMAChartEditPage() {
  const { patientId, appointmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [intake, setIntake] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  
  // Form state
  const [vitals, setVitals] = useState({
    blood_pressure: '',
    heart_rate: '',
    temperature: '',
    respiratory_rate: '',
    oxygen_saturation: '',
    weight: '',
    height: ''
  });
  
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [newSymptom, setNewSymptom] = useState('');
  
  // Medications
  const [medications, setMedications] = useState([]);
  const [newMedication, setNewMedication] = useState({ name: '', dosage: '', frequency: '' });
  const [medicationSearch, setMedicationSearch] = useState('');
  const [medicationSuggestions, setMedicationSuggestions] = useState([]);
  const [loadingMedSuggestions, setLoadingMedSuggestions] = useState(false);
  
  // Pharmacy
  const [pharmacy, setPharmacy] = useState({ name: '', address: '', phone: '' });
  const [pharmacySearch, setPharmacySearch] = useState('');
  const [pharmacySuggestions, setPharmacySuggestions] = useState([]);
  const [loadingPharmacySuggestions, setLoadingPharmacySuggestions] = useState(false);
  
  // Allergies
  const [allergies, setAllergies] = useState([]);
  const [newAllergy, setNewAllergy] = useState('');
  
  // Triage notes
  const [triageNotes, setTriageNotes] = useState('');

  // Load patient and intake data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Get patient intakes
      const intakesRes = await intakeAPI.getByPatient(patientId);
      const intakes = intakesRes.data || [];
      
      if (intakes.length > 0) {
        const latestIntake = intakes[0];
        setIntake(latestIntake);
        
        // Populate form with existing data
        if (latestIntake.vital_signs) {
          setVitals(prev => ({
            ...prev,
            ...latestIntake.vital_signs
          }));
        }
        setChiefComplaint(latestIntake.chief_complaint || '');
        setSymptoms(latestIntake.symptoms || []);
        setMedications(latestIntake.medications?.map(m => 
          typeof m === 'string' ? { name: m, dosage: '', frequency: '' } : m
        ) || []);
        setAllergies(latestIntake.allergies || []);
        setTriageNotes(latestIntake.triage_notes || '');
      }
      
      // Get patient info
      const usersRes = await adminAPI.getUsers();
      const patient = usersRes.data?.find(u => u.id === patientId);
      setPatientInfo(patient);
      
    } catch (error) {
      console.error('Error loading patient data:', error);
      toast.error('Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // AI-assisted medication search
  const searchMedications = async (query) => {
    if (query.length < 2) {
      setMedicationSuggestions([]);
      return;
    }
    
    setLoadingMedSuggestions(true);
    try {
      const response = await aiAPI.chat({
        message: `List 5 common medications that start with or contain "${query}". For each, provide: name, common dosages, and typical frequency. Format as JSON array: [{"name": "...", "dosages": ["..."], "frequencies": ["..."]}]. Only return the JSON array, no other text.`,
        context: 'medication_lookup'
      });
      
      // Parse AI response
      const text = response.data?.response || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const suggestions = JSON.parse(jsonMatch[0]);
        setMedicationSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Error searching medications:', error);
      // Fallback to common medications
      setMedicationSuggestions([
        { name: 'Lisinopril', dosages: ['5mg', '10mg', '20mg'], frequencies: ['Once daily'] },
        { name: 'Metformin', dosages: ['500mg', '850mg', '1000mg'], frequencies: ['Twice daily', 'Once daily'] },
        { name: 'Atorvastatin', dosages: ['10mg', '20mg', '40mg'], frequencies: ['Once daily at bedtime'] },
        { name: 'Amlodipine', dosages: ['2.5mg', '5mg', '10mg'], frequencies: ['Once daily'] },
        { name: 'Omeprazole', dosages: ['20mg', '40mg'], frequencies: ['Once daily before breakfast'] }
      ].filter(m => m.name.toLowerCase().includes(query.toLowerCase())));
    } finally {
      setLoadingMedSuggestions(false);
    }
  };

  // AI-assisted pharmacy search
  const searchPharmacies = async (query) => {
    if (query.length < 2) {
      setPharmacySuggestions([]);
      return;
    }
    
    setLoadingPharmacySuggestions(true);
    try {
      const response = await aiAPI.chat({
        message: `List 5 pharmacies that match "${query}". Include major chains and local pharmacies. For each, provide a realistic example: name, address format, and phone. Format as JSON array: [{"name": "...", "address": "...", "phone": "..."}]. Only return the JSON array, no other text.`,
        context: 'pharmacy_lookup'
      });
      
      const text = response.data?.response || '';
      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const suggestions = JSON.parse(jsonMatch[0]);
        setPharmacySuggestions(suggestions);
      }
    } catch (error) {
      console.error('Error searching pharmacies:', error);
      // Fallback to common pharmacies
      setPharmacySuggestions([
        { name: 'CVS Pharmacy', address: '123 Main St, City, State 12345', phone: '(555) 123-4567' },
        { name: 'Walgreens', address: '456 Oak Ave, City, State 12345', phone: '(555) 234-5678' },
        { name: 'Walmart Pharmacy', address: '789 Center Blvd, City, State 12345', phone: '(555) 345-6789' },
        { name: 'Rite Aid', address: '321 Elm St, City, State 12345', phone: '(555) 456-7890' },
        { name: 'Costco Pharmacy', address: '654 Industrial Way, City, State 12345', phone: '(555) 567-8901' }
      ].filter(p => p.name.toLowerCase().includes(query.toLowerCase())));
    } finally {
      setLoadingPharmacySuggestions(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (medicationSearch) searchMedications(medicationSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [medicationSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pharmacySearch) searchPharmacies(pharmacySearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [pharmacySearch]);

  // Add medication
  const addMedication = (med) => {
    if (med.name) {
      setMedications([...medications, { 
        name: med.name, 
        dosage: med.dosage || med.dosages?.[0] || '', 
        frequency: med.frequency || med.frequencies?.[0] || '' 
      }]);
      setNewMedication({ name: '', dosage: '', frequency: '' });
      setMedicationSearch('');
      setMedicationSuggestions([]);
    }
  };

  // Remove medication
  const removeMedication = (index) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  // Select pharmacy
  const selectPharmacy = (p) => {
    setPharmacy(p);
    setPharmacySearch('');
    setPharmacySuggestions([]);
  };

  // Add symptom
  const addSymptom = () => {
    if (newSymptom.trim() && !symptoms.includes(newSymptom.trim())) {
      setSymptoms([...symptoms, newSymptom.trim()]);
      setNewSymptom('');
    }
  };

  // Add allergy
  const addAllergy = () => {
    if (newAllergy.trim() && !allergies.includes(newAllergy.trim())) {
      setAllergies([...allergies, newAllergy.trim()]);
      setNewAllergy('');
    }
  };

  // Save chart
  const saveChart = async () => {
    if (!intake) {
      toast.error('No intake record found to update');
      return;
    }
    
    setSaving(true);
    try {
      await intakeAPI.updateTriage(intake.id, {
        triage_notes: triageNotes,
        vital_signs: vitals,
        status: 'in_triage'
      });
      
      toast.success('Chart updated successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error saving chart:', error);
      toast.error('Failed to save chart');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-screen">
        <SpinnerGap size={48} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardText size={28} className="text-teal-600" />
            Patient Chart - CMA Review
          </h1>
          {patientInfo && (
            <p className="text-slate-500">
              {patientInfo.first_name} {patientInfo.last_name} | DOB: {patientInfo.date_of_birth}
            </p>
          )}
        </div>
      </div>

      {/* AI Assistant Banner */}
      <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl p-4 mb-6 flex items-center gap-3">
        <Robot size={32} weight="duotone" />
        <div>
          <p className="font-semibold">AI Virtual Assistant Active</p>
          <p className="text-sm text-teal-100">Medication names and pharmacy addresses are AI-assisted for faster, accurate entry</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Chief Complaint */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <FirstAid size={20} className="text-red-600" />
              Chief Complaint
            </h3>
            <textarea
              value={chiefComplaint}
              onChange={(e) => setChiefComplaint(e.target.value)}
              className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={3}
              placeholder="Patient's main complaint..."
            />
            
            {/* Symptoms */}
            <div className="mt-4">
              <label className="text-sm text-slate-600 mb-2 block">Symptoms</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {symptoms.map((symptom, idx) => (
                  <span key={idx} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {symptom}
                    <button onClick={() => setSymptoms(symptoms.filter((_, i) => i !== idx))} className="hover:text-red-600">
                      <Trash size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSymptom}
                  onChange={(e) => setNewSymptom(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSymptom()}
                  className="flex-1 p-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Add symptom..."
                />
                <button onClick={addSymptom} className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
                  <Plus size={18} />
                </button>
              </div>
            </div>
          </div>

          {/* Vitals */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Heart size={20} className="text-pink-600" />
              Vital Signs
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Blood Pressure</label>
                <input
                  type="text"
                  value={vitals.blood_pressure}
                  onChange={(e) => setVitals({...vitals, blood_pressure: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="120/80"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Heart Rate (bpm)</label>
                <input
                  type="number"
                  value={vitals.heart_rate}
                  onChange={(e) => setVitals({...vitals, heart_rate: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="72"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Temperature (°F)</label>
                <input
                  type="number"
                  step="0.1"
                  value={vitals.temperature}
                  onChange={(e) => setVitals({...vitals, temperature: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="98.6"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">O2 Saturation (%)</label>
                <input
                  type="number"
                  value={vitals.oxygen_saturation}
                  onChange={(e) => setVitals({...vitals, oxygen_saturation: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="98"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Respiratory Rate</label>
                <input
                  type="number"
                  value={vitals.respiratory_rate}
                  onChange={(e) => setVitals({...vitals, respiratory_rate: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="16"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Weight (lbs)</label>
                <input
                  type="number"
                  value={vitals.weight}
                  onChange={(e) => setVitals({...vitals, weight: e.target.value})}
                  className="w-full p-2 border border-slate-200 rounded-lg"
                  placeholder="150"
                />
              </div>
            </div>
          </div>

          {/* Allergies */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Warning size={20} className="text-amber-600" />
              Allergies
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {allergies.length === 0 && <span className="text-slate-400 text-sm">No known allergies</span>}
              {allergies.map((allergy, idx) => (
                <span key={idx} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                  {allergy}
                  <button onClick={() => setAllergies(allergies.filter((_, i) => i !== idx))} className="hover:text-red-900">
                    <Trash size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addAllergy()}
                className="flex-1 p-2 border border-slate-200 rounded-lg text-sm"
                placeholder="Add allergy..."
              />
              <button onClick={addAllergy} className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
                <Plus size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Medications with AI Assist */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Pill size={20} className="text-blue-600" />
              Medications
              <span className="ml-auto text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full flex items-center gap-1">
                <Robot size={12} /> AI Assisted
              </span>
            </h3>
            
            {/* Current Medications */}
            <div className="space-y-2 mb-4">
              {medications.length === 0 && <p className="text-slate-400 text-sm">No medications listed</p>}
              {medications.map((med, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{med.name}</p>
                    <p className="text-sm text-slate-600">{med.dosage} - {med.frequency}</p>
                  </div>
                  <button onClick={() => removeMedication(idx)} className="text-red-500 hover:text-red-700">
                    <Trash size={18} />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Add Medication with AI Search */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <MagnifyingGlass size={16} className="text-slate-400" />
                <span className="text-sm text-slate-600">Search medication (AI-assisted)</span>
              </div>
              <input
                type="text"
                value={medicationSearch}
                onChange={(e) => setMedicationSearch(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg"
                placeholder="Type medication name..."
              />
              
              {/* AI Suggestions */}
              {(loadingMedSuggestions || medicationSuggestions.length > 0) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {loadingMedSuggestions ? (
                    <div className="p-3 text-center text-slate-500">
                      <SpinnerGap size={20} className="animate-spin inline mr-2" />
                      AI searching...
                    </div>
                  ) : (
                    medicationSuggestions.map((sug, idx) => (
                      <button
                        key={idx}
                        onClick={() => addMedication(sug)}
                        className="w-full p-3 text-left hover:bg-teal-50 border-b border-slate-100 last:border-0"
                      >
                        <p className="font-medium text-slate-900">{sug.name}</p>
                        <p className="text-xs text-slate-500">
                          Dosages: {sug.dosages?.join(', ')} | {sug.frequencies?.join(', ')}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pharmacy with AI Assist */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Storefront size={20} className="text-purple-600" />
              Preferred Pharmacy
              <span className="ml-auto text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full flex items-center gap-1">
                <Robot size={12} /> AI Assisted
              </span>
            </h3>
            
            {/* Selected Pharmacy */}
            {pharmacy.name && (
              <div className="p-4 bg-purple-50 rounded-lg mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{pharmacy.name}</p>
                    <p className="text-sm text-slate-600">{pharmacy.address}</p>
                    <p className="text-sm text-slate-600">{pharmacy.phone}</p>
                  </div>
                  <button onClick={() => setPharmacy({ name: '', address: '', phone: '' })} className="text-red-500">
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Search Pharmacy */}
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <MagnifyingGlass size={16} className="text-slate-400" />
                <span className="text-sm text-slate-600">Search pharmacy (AI-assisted)</span>
              </div>
              <input
                type="text"
                value={pharmacySearch}
                onChange={(e) => setPharmacySearch(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg"
                placeholder="Type pharmacy name or location..."
              />
              
              {/* AI Suggestions */}
              {(loadingPharmacySuggestions || pharmacySuggestions.length > 0) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {loadingPharmacySuggestions ? (
                    <div className="p-3 text-center text-slate-500">
                      <SpinnerGap size={20} className="animate-spin inline mr-2" />
                      AI searching...
                    </div>
                  ) : (
                    pharmacySuggestions.map((p, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectPharmacy(p)}
                        className="w-full p-3 text-left hover:bg-purple-50 border-b border-slate-100 last:border-0"
                      >
                        <p className="font-medium text-slate-900">{p.name}</p>
                        <p className="text-xs text-slate-500">{p.address}</p>
                        <p className="text-xs text-slate-500">{p.phone}</p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Triage Notes */}
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <Stethoscope size={20} className="text-teal-600" />
              CMA Triage Notes
            </h3>
            <textarea
              value={triageNotes}
              onChange={(e) => setTriageNotes(e.target.value)}
              className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              rows={4}
              placeholder="Enter triage observations, notes for provider..."
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          onClick={saveChart}
          disabled={saving}
          className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
          data-testid="save-chart-btn"
        >
          {saving ? (
            <SpinnerGap size={20} className="animate-spin" />
          ) : (
            <FloppyDisk size={20} />
          )}
          Save Chart & Submit to Provider
        </button>
      </div>
    </div>
  );
}
